import { useEffect, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { Badge, Col, Row } from "react-bootstrap";
import moment from "moment";
import { AddCommasToAmount } from "../../helper/Utils";
import {
  get_schemes_by_fy_added_pending,
  get_financial_year,
  getActivity,
  getSubActivity,
  get_beneficiary_by_id,
} from "../../helper/Api";
import "./sanctionOrderModal.css";

const formatDate = (value) => {
  if (!value) return "—";
  const m = moment(value, ["YYYY-MM-DD", "DD/MM/YYYY", moment.ISO_8601], true);
  return m.isValid() ? m.format("DD-MM-YYYY") : value;
};

const formatAmt = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num) || num === 0) return "—";
  return `₹ ${AddCommasToAmount(value)}`;
};

const formatRate = (rate) => {
  if (rate === null || rate === undefined || rate === "" || rate === "other") {
    return null;
  }
  const num = Number(rate);
  if (Number.isNaN(num)) return null;
  return `${num}%`;
};

const amountFromRate = (taxable, rate) => {
  if (
    taxable === null ||
    taxable === undefined ||
    taxable === "" ||
    rate === null ||
    rate === undefined ||
    rate === "" ||
    rate === "other"
  ) {
    return 0;
  }
  const t = Number(taxable);
  const r = Number(rate);
  if (Number.isNaN(t) || Number.isNaN(r) || r === 0) return 0;
  return Math.round((t * r) / 100);
};

/** Show amount + rate% when rate exists, e.g. "₹ 1,000 (2%)" */
const formatAmtWithRate = (amount, rate, taxable) => {
  const rateLabel = formatRate(rate);
  let amt = amount;
  if (
    (amt === null || amt === undefined || amt === "" || Number(amt) === 0) &&
    rateLabel
  ) {
    amt = amountFromRate(taxable, rate);
  }
  const amtLabel = formatAmt(amt);
  if (!rateLabel) return amtLabel;
  if (amtLabel === "—") return rateLabel;
  return `${amtLabel} (${rateLabel})`;
};

const display = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return value;
};

const InfoItem = ({ label, value }) => (
  <div className="so-info-item">
    <div className="so-info-label">{label}</div>
    <div className="so-info-value">{value}</div>
  </div>
);

const DocLink = ({ fileList, url, label }) => {
  const hasNewFile = fileList?.[0] instanceof File;
  if (!hasNewFile && !url) return null;
  return (
    <span className="so-doc-chip">
      <i className="icon-picture_as_pdf text-danger-100" />
      {url && !hasNewFile ? (
        <a href={url} target="_blank" rel="noreferrer">
          {label}
        </a>
      ) : (
        <span>{label}</span>
      )}
    </span>
  );
};

const DeductionRow = ({ items }) => (
  <Row className="g-2 so-deduction-row">
    {items.map((item) => (
      <Col xs={6} md={3} key={item.label}>
        <InfoItem label={item.label} value={item.value} />
      </Col>
    ))}
  </Row>
);

const isPositiveAmount = (value) => {
  if (value === null || value === undefined || value === "") return false;
  const num = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(num) && num !== 0;
};

const deductionItemsForInvoice = (invoice) => {
  const items = [];
  const add = (label, raw, value) => {
    if (!isPositiveAmount(raw)) return;
    items.push({ label, value: value || formatAmt(raw) });
  };
  const tdsRate =
    invoice.tds_it_rate === "other" ? invoice.tds_it_other_rate : invoice.tds_it_rate;
  const tdsBase = invoice.tds_it_taxable || invoice.taxable_amount;
  const tdsAmount = isPositiveAmount(invoice.tds_it_amount)
    ? invoice.tds_it_amount
    : amountFromRate(tdsBase, tdsRate);
  add("TDS (IT)", tdsAmount, formatAmtWithRate(tdsAmount, tdsRate, tdsBase));
  [
    ["S-GST", "s_gst_amount", "s_gst_rate", "s_gst_taxable"],
    ["C-GST", "c_gst_amount", "c_gst_rate", "c_gst_taxable"],
    ["I-GST", "i_gst_amount", "i_gst_rate", "i_gst_taxable"],
  ].forEach(([label, amountKey, rateKey, taxableKey]) => {
    const base = invoice[taxableKey] || invoice.taxable_amount;
    const amount = isPositiveAmount(invoice[amountKey])
      ? invoice[amountKey]
      : amountFromRate(base, invoice[rateKey]);
    add(label, amount, formatAmtWithRate(amount, invoice[rateKey], base));
  });
  [
    ["GIS", "gis"],
    ["NPS", "nps"],
    ["Royalty", "royalty_amount"],
    ["Additional Royalty", "additional_royalty_amount"],
    ["SMET", "smet_amount"],
    ["Penalty in Deduction", "penalty_in_deduction_amount"],
    ["Restoration", "restoration_amount"],
    ["Labour Cess", "labour_amount"],
    ["Stamp Duty", "stamp_duty_amount"],
    ["District Mineral Foundation Trust", "dmft_amount"],
    ["Other Deduction", "other_deduction"],
    ["Penalty", "penalty_amount"],
    ["Security Deposit/Retention Money", "security_deposit_amount"],
    ["Withheld", "withheld_amount"],
  ].forEach(([label, key]) => add(label, invoice[key]));
  return items;
};

const splitDeductionRows = (items) => {
  if (!items.length) return [];
  const mid = Math.ceil(items.length / 2);
  return [items.slice(0, mid), items.slice(mid)].filter((row) => row.length);
};

const NamedValue = ({ name, id }) => {
  if (!name && !id) return "—";
  if (name && id) {
    return (
      <span>
        {name}
        <span className="so-id-hint"> (#{id})</span>
      </span>
    );
  }
  return name || `#${id}`;
};

const SanctionOrderModal = ({
  showPreview,
  setShowPreview,
  formData,
  handleSubmit,
  vendorList,
}) => {
  const [schemeMap, setSchemeMap] = useState({});
  const [subheadMap, setSubheadMap] = useState({});
  const [activityMap, setActivityMap] = useState({});
  const [subActivityMap, setSubActivityMap] = useState({});
  const [fyMap, setFyMap] = useState({});
  const [accountMap, setAccountMap] = useState({});
  const [lookupsLoading, setLookupsLoading] = useState(false);

  const handleClose = () => setShowPreview(false);

  const schemeKeys = useMemo(() => {
    const keys = new Set();
    (formData?.vouchers || []).forEach((voucher) => {
      (voucher.invoices || []).forEach((invoice) => {
        (invoice.schemes || []).forEach((scheme) => {
          if (scheme?.scheme_id) {
            keys.add(`${scheme.scheme_id}|${scheme.financial_year || 0}`);
          }
        });
      });
    });
    return Array.from(keys);
  }, [formData]);

  const vendorIds = useMemo(() => {
    const ids = new Set();
    (formData?.vouchers || []).forEach((voucher) => {
      (voucher.invoices || []).forEach((invoice) => {
        if (invoice?.vendor_id) ids.add(String(invoice.vendor_id));
      });
    });
    return Array.from(ids);
  }, [formData]);

  useEffect(() => {
    if (!showPreview) return;
    let cancelled = false;

    const loadLookups = async () => {
      setLookupsLoading(true);
      try {
        const [schemeListRes, fyRes, activityRes, subActivityRes, ...vendorResList] =
          await Promise.all([
            get_schemes_by_fy_added_pending(),
            get_financial_year(),
            getActivity(),
            getSubActivity({}),
            ...vendorIds.map((id) =>
              get_beneficiary_by_id(id).catch(() => null)
            ),
          ]);

        if (cancelled) return;

        const nextSchemeMap = {};
        const nextSubheadMap = {};
        (schemeListRes?.data?.schemes || []).forEach((scheme) => {
          nextSchemeMap[String(scheme.id)] = scheme;
          (scheme.sub_heads_list || []).forEach((subhead) => {
            nextSubheadMap[String(subhead.id)] = subhead;
          });
        });

        const detailResults = await Promise.all(
          schemeKeys.map(async (key) => {
            const [schemeId, fyId] = key.split("|");
            try {
              const res = await get_schemes_by_fy_added_pending(
                schemeId,
                fyId || 0
              );
              return res?.data?.schemes?.[0] || null;
            } catch (_e) {
              return null;
            }
          })
        );

        if (cancelled) return;

        detailResults.forEach((scheme) => {
          if (!scheme) return;
          nextSchemeMap[String(scheme.id)] = {
            ...(nextSchemeMap[String(scheme.id)] || {}),
            ...scheme,
          };
          (scheme.sub_heads_list || []).forEach((subhead) => {
            nextSubheadMap[String(subhead.id)] = subhead;
          });
        });

        const nextFyMap = {};
        (fyRes?.data?.list || []).forEach((fy) => {
          nextFyMap[String(fy.id)] = fy.year || `${fy.start_year}-${fy.end_year}`;
        });

        const nextActivityMap = {};
        (activityRes?.data?.result || []).forEach((item) => {
          nextActivityMap[String(item.id)] = item.activity || item.name;
        });

        const nextSubActivityMap = {};
        (subActivityRes?.data?.result || []).forEach((item) => {
          nextSubActivityMap[String(item.id)] =
            item.subactivity || item.name;
        });

        const nextAccountMap = {};
        vendorResList.forEach((res) => {
          const beneficiary = res?.data?.list?.[0];
          (beneficiary?.beneficiary_accounts || []).forEach((acc) => {
            nextAccountMap[String(acc.id)] = acc;
          });
        });

        setSchemeMap(nextSchemeMap);
        setSubheadMap(nextSubheadMap);
        setFyMap(nextFyMap);
        setActivityMap(nextActivityMap);
        setSubActivityMap(nextSubActivityMap);
        setAccountMap(nextAccountMap);
      } catch (_e) {
        // Keep IDs if lookups fail
      } finally {
        if (!cancelled) setLookupsLoading(false);
      }
    };

    loadLookups();
    return () => {
      cancelled = true;
    };
  }, [showPreview, schemeKeys, vendorIds]);

  const getBeneficiaryName = (id) => {
    if (!id) return "—";
    const beneficiary = (vendorList || []).find(
      (item) => String(item.id) === String(id)
    );
    return (
      beneficiary?.company_name ||
      beneficiary?.beneficiary_name ||
      "—"
    );
  };

  const getAccount = (accountId) => {
    if (!accountId) return null;
    return accountMap[String(accountId)] || null;
  };

  const getSchemeLabel = (schemeId) => {
    const scheme = schemeMap[String(schemeId)];
    if (!scheme) return display(schemeId);
    const name = scheme.name || scheme.scheme_name;
    const code = scheme.code || scheme.scheme_code;
    const title =
      name && code ? `${name} (${code})` : name || code || null;
    return <NamedValue name={title} id={schemeId} />;
  };

  const getSubheadLabel = (subheadId) => {
    const subhead = subheadMap[String(subheadId)];
    if (!subhead) return display(subheadId);
    return <NamedValue name={subhead.name || subhead.sub_head_name} id={subheadId} />;
  };

  const getActivityLabel = (activityId) => {
    if (!activityId) return "—";
    return (
      <NamedValue name={activityMap[String(activityId)]} id={activityId} />
    );
  };

  const getSubActivityLabel = (subactivityId) => {
    if (!subactivityId) return "—";
    return (
      <NamedValue
        name={subActivityMap[String(subactivityId)]}
        id={subactivityId}
      />
    );
  };

  const getFyLabel = (fyId) => {
    if (!fyId) return "—";
    return fyMap[String(fyId)] || display(fyId);
  };

  const buildSchemeGroups = (invoice) => {
    const groups = [];
    (invoice.schemes || []).forEach((scheme, sIndex) => {
      const subheads = scheme.subheads?.length ? scheme.subheads : [{}];
      subheads.forEach((subhead, shIndex) => {
        const activities =
          subhead.activity?.filter(
            (act) =>
              act?.activity_id || act?.subactivity_id || act?.balance
          ) || [];
        groups.push({
          key: `sch-${sIndex}-${shIndex}`,
          scheme,
          subhead,
          activities,
        });
      });
    });
    return groups;
  };

  const invoiceCount = (formData?.vouchers || []).reduce(
    (sum, voucher) => sum + (voucher.invoices?.length || 0),
    0
  );

  return (
    <Modal
      size="xl"
      show={showPreview}
      onHide={handleClose}
      centered
      scrollable
      dialogClassName="so-preview-modal"
    >
      <Modal.Header closeButton className="so-preview-header">
        <Modal.Title>Sanction Order Preview</Modal.Title>
      </Modal.Header>

      <Modal.Body className="so-preview-body">
        <section className="so-block">
          <div className="so-block-title-row">
            <h6 className="so-block-title">Sanction details</h6>
            <div className="so-block-badges">
              <span className="so-soft-badge">
                {formData?.vouchers?.length || 0} Voucher(s)
              </span>
              <span className="so-soft-badge">{invoiceCount} Invoice(s)</span>
              <DocLink
                fileList={formData?.sanction_order}
                url={formData?.sanction_order_url}
                label="Sanction Order"
              />
            </div>
          </div>
          <Row className="g-2">
            <Col md={4}>
              <InfoItem
                label="Sanction Order No."
                value={display(formData?.sanction_order_no)}
              />
            </Col>
            <Col md={4}>
              <InfoItem
                label="Sanction Order Date"
                value={formatDate(formData?.sanction_order_date)}
              />
            </Col>
            <Col md={4}>
              <InfoItem
                label="Sanction Order Value"
                value={formatAmt(formData?.sanction_order_value)}
              />
            </Col>
          </Row>
        </section>

        {(formData?.vouchers || []).map((voucher, voucherIndex) => (
          <section className="so-block" key={`voucher-${voucherIndex}`}>
            <div className="so-block-title-row">
              <h6 className="so-block-title">Voucher {voucherIndex + 1}</h6>
              <DocLink
                fileList={voucher.voucher}
                url={voucher.voucher_url}
                label="Voucher doc"
              />
            </div>
            <Row className="g-2">
              <Col md={4}>
                <InfoItem label="Voucher No." value={display(voucher.voucher_no)} />
              </Col>
              <Col md={4}>
                <InfoItem
                  label="Voucher Date"
                  value={formatDate(voucher.voucher_date)}
                />
              </Col>
              <Col md={4}>
                <InfoItem
                  label="Total Voucher Value"
                  value={formatAmt(voucher.total_voucher_value)}
                />
              </Col>
            </Row>

            {(voucher.invoices || []).map((invoice, invoiceIndex) => (
              <div
                className="so-invoice-card"
                key={`invoice-${voucherIndex}-${invoiceIndex}`}
              >
                <div className="so-invoice-head">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <strong>Invoice {invoiceIndex + 1}</strong>
                    <span className="text-muted">
                      {display(invoice.invoice_no)}
                    </span>
                    <Badge
                      bg={Number(invoice.payment) === 2 ? "warning" : "primary"}
                      className="so-payment-badge"
                    >
                      {Number(invoice.payment) === 2
                        ? "Part Payment"
                        : "Full Payment"}
                    </Badge>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <DocLink
                      fileList={invoice.invoice}
                      url={invoice.invoice_url}
                      label="Invoice Order"
                    />
                    <DocLink
                      fileList={invoice.invoice_ref}
                      url={invoice.invoice_ref_url}
                      label="Invoice Ref"
                    />
                    <strong className="so-invoice-amount">
                      {formatAmt(invoice.sanction_amount || invoice.invoice_value)}
                    </strong>
                  </div>
                </div>

                <Row className="g-2">
                  <Col md={4}>
                    <InfoItem
                      label="Beneficiary"
                      value={getBeneficiaryName(invoice.vendor_id)}
                    />
                  </Col>
                  <Col md={4}>
                    <InfoItem
                      label="Account Holder"
                      value={display(
                        getAccount(invoice.vendor_account_id)?.beneficiary_name
                      )}
                    />
                  </Col>
                  <Col md={4}>
                    <InfoItem
                      label="Account No."
                      value={display(
                        getAccount(invoice.vendor_account_id)?.account_no
                      )}
                    />
                  </Col>
                  <Col md={4}>
                    <InfoItem
                      label="Bank"
                      value={display(
                        getAccount(invoice.vendor_account_id)?.bank_name
                      )}
                    />
                  </Col>
                  <Col md={4}>
                    <InfoItem
                      label="Branch"
                      value={display(
                        getAccount(invoice.vendor_account_id)?.branch_name
                      )}
                    />
                  </Col>
                  <Col md={4}>
                    <InfoItem
                      label="IFSC"
                      value={display(
                        getAccount(invoice.vendor_account_id)?.ifsc_code
                      )}
                    />
                  </Col>
                  <Col md={4}>
                    <InfoItem
                      label="Invoice No."
                      value={display(invoice.invoice_no)}
                    />
                  </Col>
                  <Col md={4}>
                    <InfoItem
                      label="Invoice Date"
                      value={formatDate(invoice.invoice_date)}
                    />
                  </Col>
                  <Col md={4}>
                    <InfoItem
                      label="Invoice Value"
                      value={formatAmt(invoice.invoice_value)}
                    />
                  </Col>
                  <Col md={4}>
                    <InfoItem
                      label="Taxable Amount"
                      value={formatAmt(invoice.taxable_amount)}
                    />
                  </Col>
                  <Col md={4}>
                    <InfoItem
                      label="Sanctioned Amount"
                      value={formatAmt(invoice.sanction_amount)}
                    />
                  </Col>
                </Row>

                {(() => {
                  const deductionRows = splitDeductionRows(deductionItemsForInvoice(invoice));
                  const remarks = String(invoice.deduction_remarks || "").trim();
                  if (!deductionRows.length && !remarks) return null;
                  return (
                    <>
                      <h6 className="so-subtitle">Deductions</h6>
                      {deductionRows.map((row, rowIndex) => (
                        <DeductionRow items={row} key={`deduction_row_${rowIndex}`} />
                      ))}
                      {remarks ? (
                        <InfoItem label="Other Remarks" value={remarks} />
                      ) : null}
                    </>
                  );
                })()}

                <div className="so-subtitle-row">
                  <h6 className="so-subtitle mb-0">Scheme details</h6>
                  {lookupsLoading && (
                    <span className="so-lookup-hint">Loading names…</span>
                  )}
                </div>
                <div className="so-scheme-stack">
                  {(() => {
                    const groups = buildSchemeGroups(invoice);
                    if (groups.length === 0) {
                      return (
                        <div className="so-scheme-empty text-muted">
                          No scheme mapped
                        </div>
                      );
                    }
                    return groups.map(({ key, scheme, subhead, activities }) => (
                      <div className="so-scheme-card" key={key}>
                        <div className="so-scheme-section">
                          <div className="so-scheme-section-label">Scheme</div>
                          <Row className="g-2">
                            <Col xs={12}>
                              <InfoItem
                                label="Name"
                                value={getSchemeLabel(scheme.scheme_id)}
                              />
                            </Col>
                            <Col xs={6} md={4}>
                              <InfoItem
                                label="FY"
                                value={getFyLabel(scheme.financial_year)}
                              />
                            </Col>
                            <Col xs={6} md={4}>
                              <InfoItem
                                label="Amount"
                                value={formatAmt(scheme.amount)}
                              />
                            </Col>
                          </Row>
                        </div>

                        <div className="so-scheme-section">
                          <div className="so-scheme-section-label">Subhead</div>
                          <Row className="g-2">
                            <Col xs={12}>
                              <InfoItem
                                label="Name"
                                value={getSubheadLabel(subhead.sub_heads_id)}
                              />
                            </Col>
                            <Col xs={6} md={4}>
                              <InfoItem
                                label="Amount"
                                value={formatAmt(subhead.sub_head_amount)}
                              />
                            </Col>
                          </Row>
                        </div>

                        {activities.length > 0 && (
                          <div className="so-scheme-section so-scheme-section-last">
                            <div className="so-scheme-section-label">
                              Activity / Subactivity
                              {activities.length > 1
                                ? ` (${activities.length})`
                                : ""}
                            </div>
                            <div className="so-activity-list">
                              {activities.map((act, aIndex) => (
                                <div
                                  className="so-activity-item"
                                  key={`${key}-act-${aIndex}`}
                                >
                                  {activities.length > 1 && (
                                    <div className="so-activity-item-head">
                                      #{aIndex + 1}
                                    </div>
                                  )}
                                  <Row className="g-2">
                                    <Col xs={12}>
                                      <InfoItem
                                        label="Activity"
                                        value={getActivityLabel(
                                          act.activity_id
                                        )}
                                      />
                                    </Col>
                                    <Col xs={12}>
                                      <InfoItem
                                        label="Subactivity"
                                        value={getSubActivityLabel(
                                          act.subactivity_id
                                        )}
                                      />
                                    </Col>
                                    <Col xs={6} md={4}>
                                      <InfoItem
                                        label="Release"
                                        value={formatAmt(act.balance)}
                                      />
                                    </Col>
                                    <Col xs={6} md={4}>
                                      <InfoItem
                                        label="Deduction"
                                        value={formatAmt(
                                          act.activity_deduction
                                        )}
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            ))}
          </section>
        ))}
      </Modal.Body>

      <Modal.Footer className="so-preview-footer">
        <Button variant="outline-secondary" size="sm" onClick={handleClose}>
          Change
        </Button>
        <Button className="so-preview-submit" size="sm" onClick={handleSubmit}>
          Submit for Approval
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SanctionOrderModal;
